import { mandatoryWarning, withWarningTargets } from '../warnings';
export { getActiveWarnings } from '../warnings';
import { MatrixAxis } from '@texas/api/endpoints/compositionGroup/compositionGroupMatrixApi';
import { MatrixNode } from '@texas/api/endpoints/matrixConfigurationApi';
import { ProductGroup } from '@texas/api/endpoints/metadata/productGroupsApi';
import { articleDescriptionMaxLength } from '@texas/resources/constants';
import { TFunction } from 'i18next';
import { ProposedArticleOptions } from './ProposedArticleCard';

interface FormWarningContext {
  customerId: number;
  hasTemplate: boolean;
  itemCode: string;
  invalidLength: boolean;
  minimumLength: number;
  maximumLength: number;
  name: string;
  hasNewArticles: boolean;
  branchIds: number[];
  mode: 'single' | 'range';
  targetCount: number;
}

interface ArticleWarningContext {
  options: ProposedArticleOptions;
  products: ProductGroup[];
  xNode?: MatrixNode;
  yNode?: MatrixNode;
  erpConflict: boolean;
}

export function getFormWarnings(
  context: FormWarningContext,
  t: TFunction,
  scope = 'form',
) {
  return withWarningTargets(
    {
      customer: mandatoryWarning(
        !context.customerId,
        t('article.create.customerRequired'),
      ),
      template: mandatoryWarning(
        !context.hasTemplate,
        t('article.create.templateRequired'),
      ),
      itemCode: mandatoryWarning(
        !context.itemCode.trim() || context.invalidLength,
        !context.itemCode.trim()
          ? t('article.create.itemCodeRequired')
          : t('article.copy.itemCodeLengthRequirement', {
              minimum: context.minimumLength,
              maximum: context.maximumLength,
            }),
      ),
      description: mandatoryWarning(
        !context.name.trim() ||
          context.name.trim().length > articleDescriptionMaxLength,
        t('article.create.descriptionRequired', {
          maximum: articleDescriptionMaxLength,
        }),
      ),
      branches: mandatoryWarning(
        context.hasNewArticles && !context.branchIds.length,
        t('article.create.branchesRequired'),
      ),
      range: mandatoryWarning(
        context.mode === 'range' &&
          !!context.itemCode.trim() &&
          !context.targetCount,
        t('article.create.rangeLimit'),
      ),
    },
    scope,
  );
}

export function getArticleWarnings(
  { options, products, xNode, yNode, erpConflict }: ArticleWarningContext,
  t: TFunction,
  scope = 'article',
) {
  const requiresMatrix = !erpConflict && !!(xNode ?? yNode);
  return withWarningTargets(
    {
      productGroup: mandatoryWarning(
        !products.some(
          (product) =>
            !product.archived && product.id === options.productGroupId,
        ),
        t('article.create.productGroupRequired'),
      ),
      nodeY: mandatoryWarning(
        requiresMatrix && !!yNode && !options.nodeYValues?.length,
        t('article.create.matrixValuesRequired', { name: yNode?.name }),
      ),
      nodeX: mandatoryWarning(
        requiresMatrix && !!xNode && !options.nodeXValues?.length,
        t('article.create.matrixValuesRequired', { name: xNode?.name }),
      ),
      colorAxis: mandatoryWarning(
        requiresMatrix &&
          !(
            (options.colorAxis === MatrixAxis.Columns && !!xNode) ||
            (options.colorAxis === MatrixAxis.Rows && !!yNode)
          ),
        t('article.create.colorAxisRequired'),
      ),
      masterBranch: mandatoryWarning(
        erpConflict && !!options.importErp && !options.masterBranchId,
        t('article.create.selectImportBranch'),
      ),
    },
    scope,
  );
}
